import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Ghost, Moon } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/1c162913-072e-49dc-8dee-9962689a2e68/images/ce0971355985956f019ceea6b74ff43c56b7c0591207162e5ce807202c0e97ba.jpeg";

const BATS = [
  { top: "14%", dur: "16s", delay: "0s", size: 40 },
  { top: "22%", dur: "22s", delay: "3s", size: 28 },
  { top: "9%", dur: "19s", delay: "7s", size: 34 },
  { top: "30%", dur: "25s", delay: "11s", size: 24 },
  { top: "18%", dur: "20s", delay: "14s", size: 30 },
];

function BatIcon({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 32" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M32 8c1.2-2.4 2.6-4 4-4 0 1.6.6 2.8 1.6 3.6C40 4.4 44 2 48 4c-1 1.4-1.2 3-.8 4.6C50 6 55 6 60 10c-4 0-6 1.4-7 3.4-.8 1.6-2.4 2.6-4.4 2.6-2.4 0-3.6 1.2-4.8 3-1 1.6-2.4 3-4.8 3-2.2 0-3.6-1.4-4.4-3-.4-.8-1-1.2-1.8-1.2s-1.4.4-1.8 1.2c-.8 1.6-2.2 3-4.4 3-2.4 0-3.8-1.4-4.8-3-1.2-1.8-2.4-3-4.8-3-2 0-3.6-1-4.4-2.6C11 11.4 9 10 5 10c5-4 10-4 12.8-1.4.4-1.6.2-3.2-.8-4.6C21 2 25 4.4 26.4 7.6 27.4 6.8 28 5.6 28 4c1.4 0 2.8 1.6 4 4z" />
    </svg>
  );
}

function getHalloweenTarget() {
  // Countdown ends when Halloween ends: midnight that closes Oct 31 (Nov 1, 00:00).
  const now = new Date();
  let year = now.getFullYear();
  let target = new Date(year, 10, 1, 0, 0, 0); // Nov 1 of this year = end of Oct 31
  if (now >= target) target = new Date(year + 1, 10, 1, 0, 0, 0);
  return target;
}

function useCountdown() {
  const [remaining, setRemaining] = useState(() => getHalloweenTarget() - new Date());

  useEffect(() => {
    const id = setInterval(() => setRemaining(getHalloweenTarget() - new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const total = Math.max(remaining, 0);
  const days = Math.floor(total / 86400000);
  const hours = Math.floor((total % 86400000) / 3600000);
  const minutes = Math.floor((total % 3600000) / 60000);
  const seconds = Math.floor((total % 60000) / 1000);
  return { days, hours, minutes, seconds };
}

function CountdownUnit({ value, label }) {
  return (
    <div className="flex flex-col items-center" data-testid={`countdown-${label.toLowerCase()}`}>
      <span className="font-display text-4xl sm:text-5xl md:text-6xl leading-none text-transparent bg-clip-text bg-gradient-to-b from-orange-200 to-orange-500 tabular-nums drop-shadow-[0_4px_20px_rgba(0,0,0,0.8)]">
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-orange-300/70 text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] mt-2">
        {label}
      </span>
    </div>
  );
}

export default function Halloween() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { days, hours, minutes, seconds } = useCountdown();

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/products?collection=halloween&sort=newest")
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-20 bg-[#080b09] hw-cursor" data-testid="halloween-page">
      {/* HERO */}
      <section className="relative h-[90vh] overflow-hidden">
        <img src={HERO} alt="Abandoned haunted tennis court" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-[#080b09]" />

        <div className="fog-layer fog-1" />
        <div className="fog-layer fog-2" />
        <div className="fog-bottom" />

        {BATS.map((b, i) => (
          <span
            key={`bat-${i}`}
            className="bat-fly"
            style={{ top: b.top, left: 0, animationDuration: b.dur, animationDelay: b.delay }}
            data-testid={`halloween-bat-${i}`}
          >
            <BatIcon size={b.size} />
          </span>
        ))}

        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <span className="inline-flex items-center gap-2 border border-orange-400/60 text-orange-300 text-xs font-bold uppercase tracking-[0.3em] px-4 py-2 mb-6 backdrop-blur-sm animate-fade-up opacity-0">
            <Moon size={14} className="moon-glow" /> Limited Edition
          </span>
          <h1 className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-9xl leading-[0.85] uppercase text-transparent bg-clip-text bg-gradient-to-b from-orange-200 to-orange-500 animate-fade-up opacity-0 drop-shadow-[0_4px_30px_rgba(0,0,0,0.8)]" style={{ animationDelay: "100ms" }}>
            Nighttime
          </h1>
          <p className="text-cream/80 max-w-xl mt-6 text-base leading-relaxed animate-fade-up opacity-0" style={{ animationDelay: "220ms" }}>
            The court's been abandoned for years. Fog rolls over the cracks, dead leaves drift past a
            net that still sways in the wind, and the balls never got picked up. Grab the drop before
            it disappears at midnight.
          </p>
          <div
            className="flex items-start gap-4 sm:gap-8 mt-10 animate-fade-up opacity-0"
            style={{ animationDelay: "300ms" }}
            data-testid="halloween-countdown"
          >
            <CountdownUnit value={days} label="Days" />
            <span className="font-display text-4xl sm:text-6xl text-orange-500/40 leading-none">:</span>
            <CountdownUnit value={hours} label="Hours" />
            <span className="font-display text-4xl sm:text-6xl text-orange-500/40 leading-none">:</span>
            <CountdownUnit value={minutes} label="Minutes" />
            <span className="font-display text-4xl sm:text-6xl text-orange-500/40 leading-none">:</span>
            <CountdownUnit value={seconds} label="Seconds" />
          </div>
          <a href="#drop" className="mt-8 bg-orange-500 text-black px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-orange-400 transition-colors animate-fade-up opacity-0" style={{ animationDelay: "340ms" }} data-testid="halloween-cta">
            Enter If You Dare
          </a>
        </div>
      </section>

      {/* DROP */}
      <section id="drop" className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="text-center mb-12">
          <h2 className="font-display text-4xl sm:text-5xl uppercase text-orange-400">The Nighttime Drop</h2>
          <p className="text-cream/50 text-xs font-bold uppercase tracking-[0.25em] mt-3">Limited Edition — Here 'Til Midnight</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => <div key={i} className="aspect-[4/5] bg-white/5 animate-pulse" />)}
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center text-center gap-5 py-16" data-testid="halloween-empty">
            <Ghost size={56} className="text-orange-400/80" />
            <h3 className="font-display text-3xl uppercase text-cream">The Drop Rises Soon…</h3>
            <p className="text-cream/50 max-w-md">
              Something wicked is coming to the court. Add products to the Halloween collection in Shopify
              and they'll claw their way onto this page.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-6 md:gap-y-12">
            {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
